import { EmployeeDecisions } from "./EmployeeDecisions";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ZoomableView, WaShareButton, ltrNums } from "./FilePreviewTools";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { EmployeeEmailLog } from "./EmployeeEmailLog";
import {
  Eye, Download, Loader2, User, FileText, BarChart3, CalendarCheck, History, Car, Bike, KeyRound, Copy, Check, Camera, X, CreditCard, Mail, Gavel, MessagesSquare,
} from "lucide-react";
import { EmployeeCorrTab } from "./corr/EmployeeCorrTab";
import { EmployeeRentedSection } from "./EmployeeRentedSection";
import { useAuth } from "../context/AuthContext";
import { SupervisorBadge } from "./SupervisorBadge";
import { Button } from "../components/ui/button";
import { DOC_TYPES, STATUS_LABELS, STATUS_STYLES } from "../lib/constants";
import { fileDownloadUrl, fileUrl, employeeDocsZipUrl, api } from "../lib/api";
import { StatusIcon, Countdown } from "./DocStatus";
import { ResignCountdown } from "./ResignCountdown";
import { LeaveBalanceSection } from "./LeaveBalanceSection";
import { DisciplinarySection } from "./DisciplinarySection";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { PerformanceTab, AttendanceTab, ActivityTab } from "./EmployeePerfPanels";
import { OperatingCardsDialog } from "./OperatingCardsDialog";
import { PendingDocsCard } from "./PendingDocsCard";
import { useEmployeeCustomFields } from "./EmployeeCustomFieldsInputs";
import { RenewIqamaDialog } from "./RenewIqamaDialog";

const InfoRow = ({ label, value, ltr }) => (
  <div className="flex items-baseline gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 text-sm w-28 sm:w-32 shrink-0 leading-tight">{label}</span>
    <span className={`text-slate-800 font-medium text-sm flex-1 min-w-0 break-words ${ltr ? "font-mono-nums text-left" : ""}`}>{value || "—"}</span>
  </div>
);

const DocMeta = ({ doc }) => (
  doc.expiry_date ? (
    <>
      <StatusIcon status={doc.status} />
      <span className="font-mono-nums text-sm text-slate-600">{doc.expiry_date}</span>
      <Countdown doc={doc} />
      <Badge variant="outline" className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[doc.status]}`}>
        {STATUS_LABELS[doc.status]}
      </Badge>
    </>
  ) : (
    <span className="text-xs text-slate-400">لا يوجد تاريخ</span>
  )
);

export const EmployeeDetailDialog = ({ open, onOpenChange, employee, editable = false, onUpdated }) => {
  const { user: authUser } = useAuth() || {};
  const canCorr = ["manager", "hr_manager", "operations_manager", "supervisor"].includes(authUser?.role);

  const [preview, setPreview] = useState(null); // { url, type, name }
  const [loadingId, setLoadingId] = useState(null);
  const [perf, setPerf] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [vehicles, setVehicles] = useState(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [iqExp, setIqExp] = useState({});

  const empId = employee?.id;
  const [pwBusy, setPwBusy] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [pwCopied, setPwCopied] = useState(false);
  const [photoActionBusy, setPhotoActionBusy] = useState(false);
  const [photoDone, setPhotoDone] = useState(false);
  const [opCardsOpen, setOpCardsOpen] = useState(false);
  const customFields = useEmployeeCustomFields(open);

  const approvePhoto = async () => {
    setPhotoActionBusy(true);
    try { await api.approveEmployeePhoto(empId); toast.success("تمت الموافقة على الصورة"); setPhotoDone(true); onUpdated && onUpdated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ العملية"); }
    finally { setPhotoActionBusy(false); }
  };
  const rejectPhoto = async () => {
    setPhotoActionBusy(true);
    try { await api.rejectEmployeePhoto(empId); toast.success("تم رفض الصورة"); setPhotoDone(true); onUpdated && onUpdated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ العملية"); }
    finally { setPhotoActionBusy(false); }
  };

  const resetPortalPw = async () => {
    if (!empId) return;
    if (!window.confirm(`سيتم إنشاء كلمة مرور مؤقتة جديدة لدخول ${employee.name} على بوابة الموظف، وإلغاء كلمة المرور الحالية. متابعة؟`)) return;
    setPwBusy(true); setTempPw(""); setPwCopied(false);
    try {
      const r = await api.resetEmployeePortalPassword(empId);
      setTempPw(r.password || "");
      toast.success("تم إنشاء كلمة مرور مؤقتة — سلّمها للموظف");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إعادة التعيين"); }
    finally { setPwBusy(false); }
  };

  const copyPw = async () => {
    try { await navigator.clipboard.writeText(tempPw); setPwCopied(true); setTimeout(() => setPwCopied(false), 2000); } catch (_) {}
  };

  const loadPerf = useCallback(async () => {
    if (!empId) return;
    setPerfLoading(true);
    try { setPerf(await api.employeePerformance(empId)); }
    catch { setPerf({ rider: { matched: false }, evaluations: [], avg_score: null }); }
    finally { setPerfLoading(false); }
  }, [empId]);

  const loadVehicles = useCallback(async () => {
    if (!empId) return;
    setVehiclesLoading(true);
    try { setVehicles(await api.employeeVehicles(empId)); }
    catch { setVehicles([]); }
    finally { setVehiclesLoading(false); }
  }, [empId]);

  const loadActivity = useCallback(async () => {
    if (!empId) return;
    setActivityLoading(true);
    try { setActivity(await api.employeeActivity(empId)); }
    catch { setActivity({ logs: [], created_at: employee?.created_at }); }
    finally { setActivityLoading(false); }
  }, [empId, employee?.created_at]);

  useEffect(() => {
    if (open && empId) { loadPerf(); loadActivity(); loadVehicles(); setPhotoDone(false); }
    if (!open) { setPerf(null); setActivity(null); setVehicles(null); }
  }, [open, empId, loadPerf, loadActivity, loadVehicles]);

  const addEval = async (payload) => { await api.addEvaluation(empId, payload); await loadPerf(); await loadActivity(); };
  const deleteEval = async (evalId) => {
    try { await api.deleteEvaluation(empId, evalId); await loadPerf(); toast.success("تم حذف التقييم"); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const openPreview = async (attachmentId, name) => {
    setLoadingId(attachmentId);
    try {
      const blob = await api.getFileBlob(attachmentId);
      const isPdf = (blob.type || "").includes("pdf") || (await blob.slice(0, 4).text()) === "%PDF";
      if (isPdf) {
        const res = await api.getPdfPages(attachmentId);
        if (res.is_pdf && res.pages?.length) {
          setPreview({ pages: res.pages, type: "pdf", name, id: attachmentId });
        } else {
          // Fallback: open the file directly (browser handles it).
          window.open(fileDownloadUrl(attachmentId), "_blank");
        }
      } else if (blob.type && !blob.type.startsWith("image/") && !blob.type.includes("octet")) {
        window.open(fileDownloadUrl(attachmentId), "_blank");
      } else {
        const url = URL.createObjectURL(blob);
        setPreview({ url, type: blob.type, name, id: attachmentId });
      }
    } catch {
      toast.error("تعذّر تحميل المعاينة");
    } finally {
      setLoadingId(null);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const Attachment = ({ attachmentId, name, testid }) => (
    <>
      <button type="button" data-testid={`${testid}-preview`} disabled={loadingId === attachmentId}
        onClick={() => openPreview(attachmentId, name)}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline disabled:opacity-50">
        {loadingId === attachmentId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} معاينة
      </button>
      <a href={fileDownloadUrl(attachmentId)} data-testid={`${testid}-download`}
        className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:underline">
        <Download className="h-3.5 w-3.5" /> تنزيل
      </a>
    </>
  );

  if (!employee) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader className="text-right">
            <div className="flex items-center gap-3">
              <EmployeeAvatar employee={employee} size="lg" editable={editable} onUploaded={onUpdated} />
              <DialogTitle className="font-heading text-xl">{employee.name}</DialogTitle>
              <SupervisorBadge employee={employee} />
              {editable && (
                <button type="button" data-testid="reset-portal-pw-btn" onClick={resetPortalPw} disabled={pwBusy}
                  className="ms-auto inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50">
                  {pwBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} كلمة مرور البوابة
                </button>
              )}
            </div>
            {tempPw && (
              <div data-testid="temp-pw-box" className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-right">
                <p className="text-xs text-emerald-700 mb-1.5">كلمة المرور المؤقتة (سلّمها للموظف — سيُطلب منه تغييرها عند أول دخول):</p>
                <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-emerald-200 px-3 py-2">
                  <code data-testid="temp-pw-value" className="font-mono-nums text-base font-bold text-slate-800" dir="ltr">{tempPw}</code>
                  <button type="button" data-testid="copy-temp-pw" onClick={copyPw}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline">
                    {pwCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {pwCopied ? "تم النسخ" : "نسخ"}
                  </button>
                </div>
              </div>
            )}
          </DialogHeader>

          {editable && employee.pending_photo_id && !photoDone && (
            <div data-testid="pending-photo-card" className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
              <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                <Camera className="h-3.5 w-3.5" /> طلب صورة شخصية جديدة من الموظف — بانتظار موافقتك
              </p>
              <div className="flex items-center gap-3">
                <img src={fileUrl(employee.pending_photo_id)} alt="pending" className="h-20 w-20 rounded-xl object-cover border border-blue-200 bg-white" />
                <div className="flex flex-col gap-2">
                  <Button size="sm" data-testid="approve-photo-btn" disabled={photoActionBusy} onClick={approvePhoto} className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                    {photoActionBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} موافقة
                  </Button>
                  <Button size="sm" variant="outline" data-testid="reject-photo-btn" disabled={photoActionBusy} onClick={rejectPhoto} className="h-8 gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50">
                    <X className="h-3.5 w-3.5" /> رفض
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Tabs defaultValue="overview" dir="rtl" className="mt-2 min-w-0">
            <div className="w-full min-w-0 overflow-x-auto no-scrollbar">
              <TabsList data-testid="employee-tabs" className="inline-flex w-max flex-nowrap justify-start">
                <TabsTrigger value="overview" data-testid="emp-tab-overview" className="shrink-0 gap-1"><User className="h-3.5 w-3.5" /> نظرة عامة</TabsTrigger>
                <TabsTrigger value="documents" data-testid="emp-tab-documents" className="shrink-0 gap-1"><FileText className="h-3.5 w-3.5" /> الوثائق</TabsTrigger>
                <TabsTrigger value="decisions" data-testid="emp-tab-decisions" className="shrink-0 gap-1"><Gavel className="h-3.5 w-3.5" /> القرارات</TabsTrigger>
                <TabsTrigger value="vehicles" data-testid="emp-tab-vehicles" className="shrink-0 gap-1"><Car className="h-3.5 w-3.5" /> المركبات</TabsTrigger>
                <TabsTrigger value="performance" data-testid="emp-tab-performance" className="shrink-0 gap-1"><BarChart3 className="h-3.5 w-3.5" /> الأداء</TabsTrigger>
                <TabsTrigger value="attendance" data-testid="emp-tab-attendance" className="shrink-0 gap-1"><CalendarCheck className="h-3.5 w-3.5" /> الحضور</TabsTrigger>
                <TabsTrigger value="activity" data-testid="emp-tab-activity" className="shrink-0 gap-1"><History className="h-3.5 w-3.5" /> النشاط</TabsTrigger>
                <TabsTrigger value="emails" data-testid="emp-tab-emails" className="shrink-0 gap-1"><Mail className="h-3.5 w-3.5" /> المرسل بالإيميل</TabsTrigger>
                {canCorr && <TabsTrigger value="corr" data-testid="emp-tab-corr" className="shrink-0 gap-1"><MessagesSquare className="h-3.5 w-3.5" /> مراسلات</TabsTrigger>}
              </TabsList>
            </div>

            <TabsContent value="overview" className="mt-4 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 min-w-0">
                <InfoRow label="الفرع" value={employee.branch} />
                <InfoRow label="القسم" value={employee.department} />
                <InfoRow label="المسمى الوظيفي" value={employee.job_title} />
                <InfoRow label="رقم المعرف" value={employee.rider_id} />
                <InfoRow label="الرقم الوظيفي (معرف ثاني)" value={employee.employee_number} />
                <InfoRow label="رقم الإقامة / الهوية" value={employee.iqama_number} ltr />
                {editable && employee.nationality && !/سعود|saudi/i.test(employee.nationality) && <InfoRow label="انتهاء الإقامة" value={
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-mono-nums" dir="ltr">{iqExp[employee.id] || employee.documents?.iqama?.expiry_date || "—"}</span>
                    <button type="button" onClick={() => setRenewOpen(true)} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="detail-renew-iqama">تم التجديد</button>
                    <RenewIqamaDialog open={renewOpen} onOpenChange={setRenewOpen} employees={[{ id: employee.id, name: employee.name, expiry: (iqExp[employee.id] || employee.documents?.iqama?.expiry_date || "").slice(0, 10) }]}
                      onDone={(d) => { const n = d?.employees?.[0]?.new_expiry; if (n) setIqExp((p) => ({ ...p, [employee.id]: n })); onUpdated?.(); }} />
                  </span>} />}
                <InfoRow label="رقم الجوال" value={employee.phone || employee.notify_phone} ltr />
                <InfoRow label="الجنسية" value={employee.nationality} />
                <InfoRow label="المهنة" value={employee.profession} />
                <InfoRow label="تاريخ الميلاد" value={employee.birth_date} ltr />
                <InfoRow label="تاريخ التعيين" value={employee.hire_date} ltr />
                <InfoRow label="رقم المركبة" value={employee.vehicle_number} ltr />
                <InfoRow label="رقم الجواز" value={employee.passport_number} ltr />
                <InfoRow label="كود البنزين" value={employee.fuel_code} ltr />
                <InfoRow label="حالة الموظف" value={employee.employee_status || employee.tashark_status} />
                <InfoRow label="نوع العقد" value={employee.contract_type} />
                <InfoRow label="رقم كرت التشغيل" value={employee.documents?.operation_card?.card_number} ltr />
                <InfoRow label="نوع المركبة في التطبيق" value={employee.app_vehicle_type} />
                <InfoRow label="العنوان الوطني المختصر" value={employee.national_address_short} ltr />
                <InfoRow label="البريد الإلكتروني" value={employee.email} ltr />
                <InfoRow label="العقد" value={employee.contract_start || employee.contract_end ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="detail-contract">
                    <span className="font-mono-nums" dir="ltr">{employee.contract_start || "—"} → {employee.contract_end || "—"}</span>
                    {employee.contract_status?.days_left != null && <Countdown doc={employee.contract_status} />}
                  </span>
                ) : null} />
                {employee.resignation_last_day && <InfoRow label="آخر يوم عمل (استقالة)" value={<ResignCountdown day={employee.resignation_last_day} />} />}
                {customFields.map((f) => <InfoRow key={f.key} label={f.label} value={employee.custom_fields?.[f.key]} ltr={f.type !== "text" && f.type !== "select"} />)}
              </div>
              <EmployeeRentedSection rented={employee.rented_accounts} />
              <LeaveBalanceSection employeeId={employee.id} canEdit={editable} />
              {editable && <DisciplinarySection employeeId={employee.id} />}
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <div className="space-y-2">
                {editable && <PendingDocsCard employee={employee} onDone={onUpdated} />}
                {(() => {
                  const hasFiles = DOC_TYPES.some((d) => employee.documents?.[d.key]?.attachment_id)
                    || (employee.custom_documents || []).some((c) => c.attachment_id);
                  return hasFiles ? (
                    <div className="flex justify-end pb-1">
                      <a href={employeeDocsZipUrl(empId)} data-testid="download-all-docs"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
                        <Download className="h-3.5 w-3.5" /> تنزيل كل الوثائق (ملف مضغوط)
                      </a>
                    </div>
                  ) : null;
                })()}
                {DOC_TYPES.map((d) => {
                  const doc = employee.documents?.[d.key] || {};
                  return (
                    <div key={d.key} data-testid={`detail-doc-${d.key}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 flex-wrap">
                      <span className="font-semibold text-slate-700 min-w-[100px]">{d.label}</span>
                      <div className="flex items-center gap-2 flex-wrap justify-end flex-1">
                        <DocMeta doc={doc} />
                        {d.key === "operation_card" && editable && (
                          <button type="button" data-testid="open-opcards-setup"
                            onClick={() => setOpCardsOpen(true)}
                            className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors">
                            <CreditCard className="h-3.5 w-3.5" /> ضبط كرت التشغيل
                          </button>
                        )}
                        {doc.attachment_id && (
                          <Attachment attachmentId={doc.attachment_id} name={doc.attachment_name} testid={`detail-doc-${d.key}`} />
                        )}
                      </div>
                    </div>
                  );
                })}
                {(employee.custom_documents || []).map((doc, i) => (
                  <div key={`c${i}`} data-testid={`detail-custom-doc-${i}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 flex-wrap">
                    <span className="font-semibold text-slate-700 min-w-[100px]">{doc.label || "وثيقة"}</span>
                    <div className="flex items-center gap-2 flex-wrap justify-end flex-1">
                      <DocMeta doc={doc} />
                      {doc.attachment_id && (
                        <Attachment attachmentId={doc.attachment_id} name={doc.attachment_name} testid={`detail-custom-doc-${i}`} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="vehicles" className="mt-4">
              {vehiclesLoading ? (
                <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : !vehicles || vehicles.length === 0 ? (
                <div className="py-10 text-center text-slate-400" data-testid="emp-vehicles-empty">لا توجد مركبات مرتبطة بهذا الموظف.</div>
              ) : (
                <div className="space-y-4" data-testid="emp-vehicles-list">
                  {vehicles.some((v) => v.current) && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-2">المركبة الحالية</p>
                      {vehicles.filter((v) => v.current).map((v) => {
                        const Icon = v.type?.includes("سيارة") ? Car : Bike;
                        return (
                          <div key={v.asset_id} data-testid={`emp-vehicle-current-${v.asset_id}`}
                            className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                            <Icon className="h-5 w-5 text-blue-600 shrink-0" />
                            <div className="flex-1">
                              <p className="font-heading font-bold text-slate-800" dir="ltr">{v.plate || "—"}</p>
                              <p className="text-xs text-slate-500">{v.type}{v.branch ? ` · ${v.branch}` : ""}</p>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200 rounded-full text-[11px]">منذ {v.received_at || "—"}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {vehicles.some((v) => !v.current) && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-2">سجل المركبات السابقة</p>
                      <div className="space-y-2">
                        {vehicles.filter((v) => !v.current).map((v, i) => {
                          const Icon = v.type?.includes("سيارة") ? Car : Bike;
                          return (
                            <div key={`${v.asset_id}-${i}`} data-testid="emp-vehicle-past"
                              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                              <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                              <div className="flex-1">
                                <p className="font-semibold text-slate-700 text-sm" dir="ltr">{v.plate || "—"}</p>
                                <p className="text-xs text-slate-400">{v.type}</p>
                              </div>
                              <span className="text-xs text-slate-400">{v.received_at} — {v.returned_at || "؟"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="performance" className="mt-4">
              <PerformanceTab data={perf} loading={perfLoading} editable={editable} onAdd={addEval} onDelete={deleteEval} />
            </TabsContent>
            <TabsContent value="attendance" className="mt-4">
              <AttendanceTab data={perf} loading={perfLoading} />
            </TabsContent>
            <TabsContent value="emails" className="mt-4">
              <EmployeeEmailLog employeeId={employee.id} />
            </TabsContent>
            {canCorr && <TabsContent value="corr" className="mt-4">
              <EmployeeCorrTab employee={employee} />
            </TabsContent>}
            <TabsContent value="decisions" className="mt-4">
              <EmployeeDecisions employeeId={employee.id} employee={employee} canEdit={editable} />
            </TabsContent>
            <TabsContent value="activity" className="mt-4">
              <ActivityTab data={activity} loading={activityLoading} createdAt={employee.created_at} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <OperatingCardsDialog open={opCardsOpen} onOpenChange={setOpCardsOpen} onSaved={onUpdated} />

      <Dialog open={Boolean(preview)} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="sm:max-w-3xl font-body max-h-[92vh] overflow-hidden" dir="rtl" data-testid="attachment-preview-dialog">
          <DialogHeader className="text-right pr-8">
            <DialogTitle className="font-heading text-lg truncate">{ltrNums(preview?.name) || "معاينة المرفق"}</DialogTitle>
          </DialogHeader>
          <div className="mt-1 space-y-2">
            {preview && <ZoomableView srcs={preview.type === "pdf" ? preview.pages : [preview.url]} alt={preview.name} maxH="max-h-[70vh]"
              extra={<WaShareButton attachmentId={preview.id} name={preview.name} mime={preview.type === "pdf" ? "application/pdf" : preview.type}
                employeeId={employee?.id} employeeName={employee?.name} />} />}
            {preview && <a href={fileDownloadUrl(preview.id)} className="block text-center text-xs text-blue-700 hover:underline py-1" data-testid="attachment-preview-download">تنزيل الملف الأصلي</a>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
